import type { Hospital } from "../app/currentHospital";

function parentOf(hospital: Hospital | { parentId?: string | null }): string | null {
  return hospital.parentId || null;
}

const CLINIC_SITE_NAME = /^(Jones Radiology|I-MED Radiology)\s+[—–-]/;

/** A clinic *location* (Orange, Dubbo, …) — not a hospital account. */
export function isClinicNetworkSite(
  hospital:
    | {
        name?: string | null;
        clinicNetworkRole?: string | null;
      }
    | null
    | undefined,
): boolean {
  if (!hospital) return false;
  if (hospital.clinicNetworkRole === "site") return true;
  return CLINIC_SITE_NAME.test((hospital.name || "").trim());
}

/** Atom / clinic-network root — a hospital account that owns many non-switchable sites. */
export function isClinicNetworkRoot(
  hospital:
    | {
        name?: string | null;
        clinicNetworkRole?: string | null;
      }
    | null
    | undefined,
): boolean {
  if (!hospital) return false;
  if (hospital.clinicNetworkRole === "root") return true;
  const name = (hospital.name || "").trim().toLowerCase();
  return name === "atom" || name === "atom radiology" || name.startsWith("atom radiology");
}

/** Brand shells and clinic sites should stay out of the hospital switcher. */
export function isClinicNetworkBrandOrSite(
  hospital:
    | {
        name?: string | null;
        clinicNetworkRole?: string | null;
      }
    | null
    | undefined,
): boolean {
  if (!hospital) return false;
  const role = hospital.clinicNetworkRole;
  if (role === "brand" || role === "site") return true;
  const name = (hospital.name || "").trim();
  if (CLINIC_SITE_NAME.test(name)) return true;
  const lower = name.toLowerCase();
  return (
    lower === "jones radiology" ||
    lower === "i-med radiology" ||
    lower === "imed radiology"
  );
}

export function hospitalSwitcherEntries(
  hospitals: Hospital[] | undefined | null,
): Hospital[] {
  return (hospitals || []).filter(
    (hospital) => !isClinicNetworkBrandOrSite(hospital),
  );
}

/** Leaf clinic sites: have a parent, and are not themselves a parent of anyone. */
export function clinicNetworkLeafSites(
  hospitals: Hospital[] | undefined | null,
): Hospital[] {
  const list = hospitals || [];
  const parentIds = new Set(
    list.map((hospital) => parentOf(hospital)).filter((id): id is string => Boolean(id)),
  );
  return list
    .filter(
      (hospital) =>
        Boolean(hospital.id) &&
        Boolean(parentOf(hospital)) &&
        !parentIds.has(hospital.id),
    )
    .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
}

/** Jones / I-MED brand shells: children of Atom, parents of clinic sites. */
export function clinicNetworkBrands(
  hospitals: Hospital[] | undefined | null,
): Hospital[] {
  const list = hospitals || [];
  const parentIds = new Set(
    list.map((hospital) => parentOf(hospital)).filter((id): id is string => Boolean(id)),
  );
  const structured = list
    .filter(
      (hospital) =>
        Boolean(hospital.id) &&
        Boolean(parentOf(hospital)) &&
        parentIds.has(hospital.id),
    )
    .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  if (structured.length) return structured;

  return list
    .filter((hospital) => {
      const name = (hospital.name || "").trim().toLowerCase();
      if (!name || name.includes(" — ") || name.includes(" - ")) return false;
      return (
        name === "jones radiology" ||
        name === "i-med radiology" ||
        name === "imed radiology"
      );
    })
    .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
}

export function clinicNetworkSitesForBrand(
  hospitals: Hospital[] | undefined | null,
  brandId: string | null | undefined,
): Hospital[] {
  if (!brandId) return [];
  return clinicNetworkLeafSites(hospitals).filter(
    (site) => parentOf(site) === brandId,
  );
}

export function clinicSiteShortLabel(siteName: string | undefined | null): string {
  const name = (siteName || "").trim();
  if (!name) return "";
  const sep = name.includes(" — ") ? " — " : name.includes(" - ") ? " - " : "";
  if (!sep) return name;
  const index = name.indexOf(sep);
  return name
    .slice(index + sep.length)
    .replace(/\s*\([A-Z]{2,3}\)\s*$/, "")
    .trim();
}

/** Atom locum posts are diagnostic radiology. Nuclear medicine is a separate specialty. */
export function isClinicNetworkSpecialtyOption(label: string | undefined | null): boolean {
  const value = (label || "").trim().toLowerCase();
  if (!value) return false;
  if (value.includes("nuclear")) return false;
  if (value.includes("interventional")) return false;
  if (value.includes("breast")) return false;
  return value === "radiology" || value.includes("diagnostic radiology") || value === "diagnostic imaging";
}

export function filterClinicNetworkSpecialties(
  options: { value: string; label: string }[],
): { value: string; label: string }[] {
  const filtered = (options || []).filter((option) =>
    isClinicNetworkSpecialtyOption(option.label),
  );
  return filtered;
}

export function pickClinicNetworkSpecialty(
  options: { value: string; label: string }[],
): string {
  const scoped = filterClinicNetworkSpecialties(options);
  const radiology = scoped.find((option) =>
    (option.label || "").trim().toLowerCase() === "radiology",
  );
  if (radiology) return radiology.value;
  const diagnostic = scoped.find((option) =>
    (option.label || "").toLowerCase().includes("diagnostic"),
  );
  if (diagnostic) return diagnostic.value;
  return scoped[0]?.value || "";
}

export function pickClinicNetworkSkill(
  options: { value: string; label: string }[],
): string {
  const needles = ["consultant", "specialist", "franvcr", "franzer", "vmo"];
  for (const needle of needles) {
    const hit = options.find((option) =>
      (option.label || "").toLowerCase().includes(needle),
    );
    if (hit) return hit.value;
  }
  return options[options.length - 1]?.value || options[0]?.value || "";
}

export function defaultContractedRateForBrand(brandName: string | undefined | null): string {
  const name = (brandName || "").toLowerCase();
  if (name.includes("jones")) return "5000";
  if (name.includes("i-med") || name.includes("imed")) return "4500";
  return "4500";
}

function isJonesBrand(brandName: string | undefined | null): boolean {
  return (brandName || "").toLowerCase().includes("jones");
}

function isImedBrand(brandName: string | undefined | null): boolean {
  const name = (brandName || "").toLowerCase();
  return name.includes("i-med") || name.includes("imed") || name.includes("i med");
}

/** Highest-need suburbs from the Jones / I-MED locum campaign. */
export function prioritySuburbNeedlesForBrand(
  brandName: string | undefined | null,
): string[] {
  if (isJonesBrand(brandName)) {
    return [
      "adelaide cbd",
      "mount barker",
      "modbury",
      "victor harbor",
      "port augusta",
      "alice springs",
    ];
  }
  if (isImedBrand(brandName)) {
    return [
      "orange",
      "dubbo",
      "northern sydney",
      "north sydney",
      "broken hill",
      "canberra",
    ];
  }
  return [];
}

export function isPriorityClinic(
  site: Hospital | undefined | null,
  brandName: string | undefined | null,
): boolean {
  if (!site) return false;
  const needles = prioritySuburbNeedlesForBrand(brandName);
  if (!needles.length) return false;
  const suburb = clinicSiteShortLabel(site.name).toLowerCase();
  return needles.some(
    (needle) => suburb === needle || suburb.startsWith(`${needle} `),
  );
}

export function pickPriorityClinicIds(
  sites: Hospital[] | undefined | null,
  brandName: string | undefined | null,
  brandId?: string | null,
): string[] {
  const scoped = (sites || []).filter((site) =>
    brandId ? parentOf(site) === brandId : true,
  );
  return scoped
    .filter((site) => isPriorityClinic(site, brandName))
    .map((site) => site.id)
    .filter(Boolean);
}

export function sortClinicsPriorityFirst(
  sites: Hospital[] | undefined | null,
  brandName: string | undefined | null,
): Hospital[] {
  return [...(sites || [])].sort((a, b) => {
    const aPriority = isPriorityClinic(a, brandName) ? 0 : 1;
    const bPriority = isPriorityClinic(b, brandName) ? 0 : 1;
    if (aPriority !== bPriority) return aPriority - bPriority;
    return clinicSiteShortLabel(a.name).localeCompare(
      clinicSiteShortLabel(b.name),
    );
  });
}

export const RADIOLOGY_SKILLS_PROFILE_URL =
  "https://script.google.com/a/macros/statdoctor.net/s/AKfycbyeIvMdP9hy9LYAV8aGxueCC1f28TrC2_d_bQUwkCOnI5xxDVWioj9U1PpyxDf9EfQs/exec";

export const RADIOLOGY_SKILLS_PROFILE_NAME = "Radiology Skills Profile";

export function locumDescriptionForBrand(
  brandName: string | undefined | null,
): string {
  if (isJonesBrand(brandName)) {
    return [
      "Locum radiology in 1-week blocks at Jones Radiology.",
      "Contracted rate $5,000 per 1-week block.",
      "Skill mix is flexible: Interventional (IR), Breast, or General imaging across sites.",
    ].join("\n\n");
  }
  if (isImedBrand(brandName)) {
    return [
      "Locum radiology in 1-week blocks at I-MED Radiology.",
      "Contracted rate $4,500 per 1-week block.",
      "Skill mix is flexible: Interventional (IR), Breast, or General imaging across sites.",
    ].join("\n\n");
  }
  return [
    "Locum radiology in 1-week blocks.",
    "Skill mix: Interventional (IR), Breast, or General imaging.",
  ].join("\n\n");
}
